import axios from 'axios';
import {
  BaseUrl,
  AuthCacheKey,
  AuthIdCacheKey,
  RequestBaseSignature,
  RequestHeaderExts,
  UuidKey,
} from '../config';
import { getFromLocalStorage, stringifyQuery } from './util';
import AppChannel from './channel';
import { getAdsExtendStr } from './adsExtend';
import { EVENT_AUTHORITY_ERROR, EVENT_LOGIN } from './event';
import { ERR_ORDER_LIMIT } from './errCode';
import { GenSignature, DecodeResContent } from './sign';
import PubSub from 'pubsub-js';

const axiosInstance = axios.create({
  baseURL: BaseUrl,
  allowAbsoluteUrls: true,
});

const genHeaders = (authInfo = {}) => {
  const uuid = getFromLocalStorage(UuidKey);
  return {
    ...RequestBaseSignature,
    ...RequestHeaderExts,
    channelId: AppChannel,
    timestamp: Date.now(),
    dsspAdsExtend: getAdsExtendStr(),
    uuid: uuid,
    ...authInfo,
  };
};

function request(url, method, config) {
  config = config || {};
  const { _auth, _silent, _loose, _json, _option, _try } = config;
  const reqConfig = { url, method };
  if (_auth) {
    const authorization = getFromLocalStorage(AuthCacheKey);
    const uid = getFromLocalStorage(AuthIdCacheKey);
    if (!authorization || !uid) {
      if (_try) {
        return Promise.resolve(null);
      }
      if (_option) {
        delete config._auth;
        delete config._option;
        return request(url, method, config);
      }
      PubSub.publish(EVENT_AUTHORITY_ERROR);
      if (!_silent) {
        PubSub.publish(EVENT_LOGIN);
      }
      const err = new Error(EVENT_AUTHORITY_ERROR);
      err.code = 401;
      return Promise.reject(err);
    }
    if (_try) {
      config.uid = uid;
    }
    delete config._auth;
    reqConfig.headers = genHeaders({ authorization, uid });
  } else {
    reqConfig.headers = genHeaders();
  }
  if (_try) {
    delete config._try;
  }
  if (_option) {
    delete config._option;
  }
  if (_silent) {
    delete config._silent;
  }
  if (_loose) {
    const uid = getFromLocalStorage(AuthIdCacheKey);
    uid && (reqConfig.headers['uid'] = uid);
    delete config._loose;
  }
  if (Object.keys(config).length) {
    if (method === 'get') {
      reqConfig.params = config;
    } else {
      if (_json) {
        reqConfig.headers['Content-Type'] = 'application/json';
        delete config._json;
      } else {
        reqConfig.headers['Content-Type'] = 'application/x-www-form-urlencoded';
      }
      reqConfig.data = config;
    }
    reqConfig.headers['X-Signature'] = GenSignature(reqConfig.headers, config);
  } else {
    reqConfig.headers['X-Signature'] = GenSignature(reqConfig.headers);
  }
  console.log('req: ', reqConfig);
  return axiosInstance.request(reqConfig).then(res => {
    const { code, message, data } = res.data;
    if (code !== 200) {
      if (_try) {
        return null;
      }
      const error = new Error(
        message || 'Something went wrong. Please try again later.'
      );
      if (code === 401) {
        PubSub.publish(EVENT_AUTHORITY_ERROR);
        if (_option) {
          return request(url, method, config);
        }
        if (!_silent) {
          PubSub.publish(EVENT_LOGIN);
        }
        error.message = EVENT_AUTHORITY_ERROR;
      } else if (code === ERR_ORDER_LIMIT) {
        error.data = DecodeResContent(data);
      }
      error.code = code;
      throw error;
    }
    return DecodeResContent(data);
  });
}

const Http = {
  get(url, config) {
    return request(url, 'get', config);
  },
  post(url, config) {
    return request(url, 'post', config);
  },
  stream(url, config) {
    config['Authorization'] = getFromLocalStorage(AuthCacheKey);
    return new EventSource(`${BaseUrl}${url}${stringifyQuery(config)}`);
  },
};

export default Http;
